<template>
  <modal
    elem-id="modal_cliente_notificacao"
    :show="isVisible"
    :title="t('Enviar notificação ao cliente')"
    :close-label="closeLabel"
    :save-label="t('Enviar')"
    :save-fn="handleEnviar"
    :save-disabled-fn="saveDisabledFn"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- v-if: keeps the editor from mounting (and the composable from being
         touched) until the modal is actually open. -->
    <template v-if="isVisible">
      <box type="info" :title="t('A mensagem vai para a caixa de entrada do portal')">
        <p>
          {{ t('O cliente vê a mensagem ao abrir o portal. Se ele tiver notificações ativadas, também recebe um aviso no celular — mas o aviso não mostra o texto, apenas avisa que há uma mensagem.') }}
        </p>
      </box>

      <input-rich-text
        elem-id="cliente_notificacao_mensagem"
        v-model="mensagem"
        :show-toolbar="true"
        :label="t('Mensagem')"
        :placeholder="t('Escreva a mensagem para o cliente')"
        :min-height="'140px'"
        data-testid="cliente-notificacao-mensagem-input"
      />

      <InlineMessage
        v-if="failureMessage"
        severity="error"
        :closable="false"
        data-testid="cliente-notificacao-erro"
      >
        {{ failureMessage }}
      </InlineMessage>

      <div v-if="isSending" class="text-center" style="margin-top: 15px;">
        <ProgressSpinner style="width: 1rem; height: 1rem" strokeWidth="4" />
        {{ t('Enviando...') }}
      </div>

      <!-- Send outcome — same three facts, worded identically, as
           PDVEntregasNotifyModal's portal channel (Trigger B). Staff read both
           screens, so the fact cannot change words depending on which one they
           see. The modal stays open (no auto-close) so the outcome stays
           legible instead of flashing a toast and disappearing. -->
      <InlineMessage
        v-if="sendOutcome === 'sent'"
        severity="success"
        :closable="false"
        data-testid="cliente-notificacao-status-avisado"
      >
        {{ t('Avisado') }}
      </InlineMessage>
      <InlineMessage
        v-else-if="sendOutcome === 'no_devices'"
        severity="warn"
        :closable="false"
        data-testid="cliente-notificacao-status-caixa-entrada"
      >
        {{ t('Na caixa de entrada — o cliente não ativou avisos no celular') }}
      </InlineMessage>
      <InlineMessage
        v-else-if="sendOutcome === 'no_portal_account'"
        severity="warn"
        :closable="false"
        data-testid="cliente-notificacao-status-guardado"
      >
        {{ t('Guardado — este cliente ainda não tem acesso ao portal') }}
      </InlineMessage>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useClienteNotificacao } from '../../composables/useClienteNotificacao';

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const { isSending, failure, result, enviar, reset } = useClienteNotificacao();

const isVisible = ref(false);
const clienteId = ref(0);
const mensagem = ref('');

/**
 * The editor emits `<p></p>` for an untouched field, so a plain `.trim()` on the
 * HTML would consider it filled. Strip the markup before deciding — the server
 * makes the same call after flattening, and disagreeing with it would let staff
 * press Enviar only to receive a 422.
 */
const hasMessage = computed(() => {
  const withoutTags = mensagem.value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  return withoutTags.trim().length > 0;
});

function saveDisabledFn(): boolean {
  return isSending.value || !hasMessage.value || clienteId.value <= 0;
}

/**
 * The sentence written next to the rule that rejected the request beats anything
 * phrased here, so it wins when present. The status-keyed fallbacks exist for
 * failures that never reached the controller (gateway HTML, timeout), where
 * `detail` is empty.
 */
const failureMessage = computed<string>(() => {
  const f = failure.value;
  if (!f) return '';
  if (f.detail) return f.detail;
  if (f.status === 503) return t('O envio de notificações está desligado neste ambiente.');
  if (f.status === 504) return t('Não foi possível falar com o portal do cliente agora. Tente de novo em alguns instantes.');
  return t('Não foi possível enviar a notificação.');
});

type EnvioOutcome = 'sent' | 'no_devices' | 'no_portal_account';

/**
 * Deliberately NOT "notificação enviada" for every case. The portal stores the
 * inbox row and answers before any push is attempted, so the only thing staff
 * can be honestly told is which of three outcomes happened — `no_devices`
 * (there's a login, but the phone won't buzz) is otherwise indistinguishable
 * from a clean send, and telling staff a phone buzzed when it didn't is a lie
 * they repeat to the customer. Same mapping, same wording, as
 * PDVEntregasNotifyModal's portal channel — not re-derived here.
 */
const sendOutcome = computed<EnvioOutcome | null>(() => {
  const warning = result.value?.warning;
  if (!result.value) return null;
  return warning === 'no_devices' || warning === 'no_portal_account' ? warning : 'sent';
});

/**
 * "Cancelar" is only honest before a send — once `sendOutcome` is set the
 * message already went out, so the same button now only dismisses the modal.
 * Leaving it reading "Cancelar" would invite staff to think pressing it undoes
 * a send that already happened.
 */
const closeLabel = computed<string>(() => (sendOutcome.value ? t('Fechar') : t('Cancelar')));

/**
 * `textoInicial` chega quando o envio partiu do mini-form da aba do portal —
 * seja de um modelo já substituído, seja de texto livre digitado lá. Sem isso o
 * atendente escreveria a mesma mensagem duas vezes. Ausente, o modal segue
 * abrindo em branco, como quando é aberto por qualquer outro caminho.
 */
const open = (id: number, textoInicial = ''): void => {
  log('ClienteNotificacaoModal', 'open', 'Opening notification modal for client', id);
  clienteId.value = id;
  mensagem.value = textoInicial;
  reset();
  isVisible.value = true;
};

const close = (): void => {
  isVisible.value = false;
  clienteId.value = 0;
  mensagem.value = '';
  reset();
  emit('close');
};

const handleClose = (): void => close();

interface ModalOpenPayload { id?: number; mensagem?: string }

const handleModalOpen = (data?: ModalOpenPayload): void => {
  if (data?.id) open(data.id, data.mensagem ?? '');
};

const handleEnviar = async (): Promise<void> => {
  if (saveDisabledFn()) return;

  const ok = await enviar(clienteId.value, mensagem.value);
  if (!ok) return; // the inline message carries the reason; the modal stays open

  // Announce the accepted send so the portal tab refreshes its history list.
  eventBus.emit('cliente-notificacao.enviada', { id: clienteId.value });

  // Nothing else to do — `sendOutcome` below reacts to `result` and renders
  // the fact. The modal stays open, same as the failure path above, instead
  // of firing a toast and closing before staff can read it.
};

onMounted(() => {
  eventBus.on('modal.cliente-notificacao.open', handleModalOpen);
  eventBus.on('modal.cliente-notificacao.close', close);
});

onBeforeUnmount(() => {
  eventBus.off('modal.cliente-notificacao.open', handleModalOpen);
  eventBus.off('modal.cliente-notificacao.close', close);
});

defineExpose({ open, close });
</script>
